import './doctor.scss';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { axiosInstance } from '../../utils/axios';
import AlertModal from '../common/AlertModal';
import DoctorPageHeader from './DoctorPageHeader';
import { groupFromPattern, MAX_AS_NEEDED_DAYS } from './prescriptionSetMapping';

// ─── 処方セット用定数 ────────────────────────────────────────────
const PARENT_GROUPS = [
  { value: 'FREQ_1', label: '分1' },
  { value: 'FREQ_2', label: '分2' },
  { value: 'FREQ_3', label: '分3' },
  { value: 'FREQ_4', label: '分4' },
  { value: 'FREQ_5', label: '分5' },
  { value: 'FREQ_6', label: '分6' },
  { value: 'FREQ_7', label: '分7' },
  { value: 'AS_NEEDED', label: '頓用' },
  { value: 'NASAL', label: '点鼻' },
  { value: 'OPHTHALMIC', label: '点眼' },
  { value: 'INHALATION', label: '吸入' },
  { value: 'PATCH', label: '貼付' },
  { value: 'TOPICAL', label: '塗布' },
  { value: 'GARGLE', label: 'うがい' },
  { value: 'EXTERNAL', label: '外用' },
  { value: 'SUBCUTANEOUS', label: '皮下注射' },
  { value: 'INTRAVENOUS', label: '静脈注射' },
  { value: 'OTHER', label: 'その他' },
];

const EXTERNAL_INJECTION_GROUPS = [
  'NASAL', 'OPHTHALMIC', 'INHALATION', 'PATCH', 'TOPICAL',
  'GARGLE', 'EXTERNAL', 'SUBCUTANEOUS', 'INTRAVENOUS',
];
const FREE_INPUT_ONLY_GROUPS = [...EXTERNAL_INJECTION_GROUPS, 'OTHER'];
const BODY_PART_REQUIRED_GROUPS = ['NASAL', 'OPHTHALMIC', 'INHALATION', 'PATCH', 'TOPICAL', 'GARGLE', 'EXTERNAL'];
const INSTRUCTION_BY_USAGE_GROUP = {
  NASAL: '点鼻', OPHTHALMIC: '点眼', INHALATION: '吸入', PATCH: '貼付',
  TOPICAL: '塗布', GARGLE: 'うがい', EXTERNAL: '外用',
  SUBCUTANEOUS: '皮下注射', INTRAVENOUS: '静脈注射', OTHER: '医師の指示通りに',
};

// 用量は小数2桁まで自由入力（1.24 / 1.67 等）。0.5刻みは使わない。
const normalizeDoseDraftInput = (raw) =>
  String(raw ?? '')
    .replace(/[．。､，]/g, '.')
    .replace(/[^\d.]/g, '');
const isDoseDraftInput = (raw) => /^[0-9]*\.?[0-9]{0,2}$/.test(raw);
// blur後は number|null。0以下は無効なので空欄扱い（0錠を許可しない）
const normalizeDoseNumber = (value) => {
  if (value === '' || value == null) return null;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 100) / 100;
};

const dosesFromGroup = (group) => {
  const m = group?.match(/^FREQ_(\d)$/);
  return m ? Number(m[1]) : null;
};

const generateLineUuid = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch (_) { /* noop */ }
  return `line_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const createEmptyPsItem = () => ({
  line_uuid: generateLineUuid(),
  catalog_item_id: undefined,
  catalog_item_name: '',
  usage_group: '',
  dosage_pattern_id: null,
  tablets_per_dose: null,
  days: null,
  memo: '',
  body_part: '',
  as_needed: false,
  normalized_amount_per_unit: 1,
  medication_unit: '錠',
});

// ─── コンポーネント ────────────────────────────────────────────
const ProfileContainer = () => {
  // ── プロフィール state ──
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [form, setForm] = useState({
    last_name: '', first_name: '',
    last_name_kana: '', first_name_kana: '',
    qualifications: '', experience_years: 0,
    profile_image_url: '', introduction: '',
    email: '', gender: '',
  });

  const [specialties, setSpecialties] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [selectedDiseaseIds, setSelectedDiseaseIds] = useState([]);
  const [selectedSymptomIds, setSelectedSymptomIds] = useState([]);

  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // ── 処方セット state ──
  const [psFeatureEnabled, setPsFeatureEnabled] = useState(false);
  const [psLoading, setPsLoading] = useState(false);
  const [personalSets, setPersonalSets] = useState([]);
  const [sharedSets, setSharedSets] = useState([]);
  const psTabLoaded = useRef(false);

  // アクションシート
  const [actionSheetSet, setActionSheetSet] = useState(null); // { id, title, editable, cloneable }
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [psDeleting, setPsDeleting] = useState(false);

  // フォームシート
  const [showPsForm, setShowPsForm] = useState(false);
  const [editingPsId, setEditingPsId] = useState(null);
  const [psFormTitle, setPsFormTitle] = useState('');
  const [psFormItems, setPsFormItems] = useState([]);
  const [dosagePatterns, setDosagePatterns] = useState([]);
  const patternsLoaded = useRef(false);
  const [medSearch, setMedSearch] = useState({});
  const [medSuggestions, setMedSuggestions] = useState({});
  const [psSaving, setPsSaving] = useState(false);
  const searchTimer = useRef(null);

  // ── AlertModal state ──
  const [alertModal, setAlertModal] = useState({
    open: false, title: '', message: '', type: 'alert', iconType: null, onConfirm: null,
  });

  const closeAlert = () => setAlertModal(prev => ({ ...prev, open: false }));
  const showAlert = (title, message) =>
    setAlertModal({ open: true, title, message, type: 'alert', iconType: null, onConfirm: null });

  // ── 初回データ読み込み ──
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [me, sp, ds, ss] = await Promise.all([
          axiosInstance.get('/api/medichat_doctor/profile'),
          axiosInstance.get('/api/medichat_doctor/masters/specialties'),
          axiosInstance.get('/api/medichat_doctor/masters/diseases'),
          axiosInstance.get('/api/medichat_doctor/masters/symptoms'),
        ]);

        if (me.data?.doctor) {
          const d = me.data.doctor;
          setForm({
            last_name: d.last_name || '',
            first_name: d.first_name || '',
            last_name_kana: d.last_name_kana || '',
            first_name_kana: d.first_name_kana || '',
            qualifications: d.qualifications || '医師免許',
            experience_years: d.experience_years || 0,
            profile_image_url: d.profile_image_url || '',
            introduction: d.introduction || '',
            email: d.email || '',
            gender: d.gender || '',
          });
          setIsActive(Boolean(d.active));
          setSelectedDiseaseIds(d.disease_ids || []);
          setSelectedSymptomIds(d.symptom_ids || []);
          if (d.profile_image_url) setPreviewUrl(d.profile_image_url);
        }

        setSpecialties(sp.data?.specialties || []);
        setDiseases(ds.data?.diseases || []);
        setSymptoms(ss.data?.symptoms || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── 処方セットタブ初回オープン時にロード ──
  useEffect(() => {
    if (activeTab === 'prescriptions' && !psTabLoaded.current) {
      psTabLoaded.current = true;
      loadPrescriptionSets();
    }
  }, [activeTab]);

  const loadPrescriptionSets = async () => {
    setPsLoading(true);
    try {
      const fsRes = await axiosInstance.get('/api/medichat_doctor/prescription_sets/feature_status');
      const enabled = fsRes.data?.enabled === true;
      setPsFeatureEnabled(enabled);
      if (enabled) {
        const psRes = await axiosInstance.get('/api/medichat_doctor/prescription_sets');
        setPersonalSets(psRes.data?.personal_items || []);
        setSharedSets(psRes.data?.shared_items || []);
      }
    } catch (_) {
      setPsFeatureEnabled(false);
    } finally {
      setPsLoading(false);
    }
  };

  const reloadPrescriptionSets = async () => {
    try {
      const psRes = await axiosInstance.get('/api/medichat_doctor/prescription_sets');
      setPersonalSets(psRes.data?.personal_items || []);
      setSharedSets(psRes.data?.shared_items || []);
    } catch (_) { /* noop */ }
  };

  const loadDosagePatternsOnce = async () => {
    if (patternsLoaded.current) return;
    try {
      const res = await axiosInstance.get('/api/medichat_doctor/masters/dosage_patterns');
      setDosagePatterns(res.data?.items || []);
      patternsLoaded.current = true;
    } catch (_) { /* noop */ }
  };

  // ── 処方セット フォーム操作 ──
  const openCreateForm = async () => {
    await loadDosagePatternsOnce();
    setEditingPsId(null);
    setPsFormTitle('');
    setPsFormItems([createEmptyPsItem()]);
    setMedSearch({});
    setMedSuggestions({});
    setShowPsForm(true);
  };

  const openEditForm = async (set) => {
    setActionSheetSet(null);
    await loadDosagePatternsOnce();
    try {
      const res = await axiosInstance.get(`/api/medichat_doctor/prescription_sets/${set.id}`);
      const detail = res.data?.item;
      const patternIndex = new Map(dosagePatterns.map(p => [p.id, p]));
      setPsFormTitle(detail.title || '');
      setPsFormItems(
        (detail.items || []).map(it => {
          const pattern = it.dosage_pattern_id ? patternIndex.get(Number(it.dosage_pattern_id)) : null;
          return {
            line_uuid: it.line_uuid || generateLineUuid(),
            catalog_item_id: it.catalog_item_id,
            catalog_item_name: it.catalog_item_name || '',
            dosage_pattern_id: it.dosage_pattern_id,
            doses_per_day: it.doses_per_day,
            tablets_per_dose: it.tablets_per_dose,
            days: it.days,
            memo: it.memo || '',
            body_part: it.body_part || '',
            as_needed: it.as_needed,
            usage_group: it.as_needed ? 'AS_NEEDED' : groupFromPattern(pattern),
            normalized_amount_per_unit: Number(it.normalized_amount_per_unit) > 0 ? Number(it.normalized_amount_per_unit) : 1,
            medication_unit: it.medication_unit || '錠',
          };
        })
      );
      setEditingPsId(detail.id);
      setMedSearch({});
      setMedSuggestions({});
      setShowPsForm(true);
    } catch (_) {
      showAlert('エラー', '処方セットの読み込みに失敗しました');
    }
  };

  const cloneSharedSet = async (set) => {
    setActionSheetSet(null);
    await loadDosagePatternsOnce();
    try {
      const res = await axiosInstance.get(`/api/medichat_doctor/prescription_sets/${set.id}`);
      const detail = res.data?.item;
      const patternIndex = new Map(dosagePatterns.map(p => [p.id, p]));
      setPsFormTitle(`${detail.title}（コピー）`);
      setPsFormItems(
        (detail.items || []).map(it => {
          const pattern = it.dosage_pattern_id ? patternIndex.get(Number(it.dosage_pattern_id)) : null;
          return {
            line_uuid: generateLineUuid(),
            catalog_item_id: it.catalog_item_id,
            catalog_item_name: it.catalog_item_name || '',
            dosage_pattern_id: it.dosage_pattern_id,
            doses_per_day: it.doses_per_day,
            tablets_per_dose: it.tablets_per_dose,
            days: it.days,
            memo: it.memo || '',
            body_part: it.body_part || '',
            as_needed: it.as_needed,
            usage_group: it.as_needed ? 'AS_NEEDED' : groupFromPattern(pattern),
            normalized_amount_per_unit: Number(it.normalized_amount_per_unit) > 0 ? Number(it.normalized_amount_per_unit) : 1,
            medication_unit: it.medication_unit || '錠',
          };
        })
      );
      setEditingPsId(null);
      setMedSearch({});
      setMedSuggestions({});
      setShowPsForm(true);
    } catch (_) {
      showAlert('エラー', '処方セットの読み込みに失敗しました');
    }
  };

  const closePsForm = () => {
    setShowPsForm(false);
    setEditingPsId(null);
    setPsFormTitle('');
    setPsFormItems([]);
    setMedSearch({});
    setMedSuggestions({});
  };

  const updatePsItem = (idx, patch) =>
    setPsFormItems(prev => prev.map((item, i) => i === idx ? { ...item, ...patch } : item));

  // 薬品検索（300ms デバウンス）
  const searchMedication = (idx, query) => {
    setMedSearch(prev => ({ ...prev, [idx]: query }));
    if (query.length < 2) {
      setMedSuggestions(prev => ({ ...prev, [idx]: [] }));
      clearTimeout(searchTimer.current);
      return;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.get(
          `/api/medichat_doctor/medications/search?q=${encodeURIComponent(query)}&limit=10`
        );
        setMedSuggestions(prev => ({ ...prev, [idx]: res.data?.items || [] }));
      } catch (_) {
        setMedSuggestions(prev => ({ ...prev, [idx]: [] }));
      }
    }, 300);
  };

  const savePrescriptionSet = async () => {
    if (!psFormTitle.trim()) {
      showAlert('入力エラー', 'セット名を入力してください');
      return;
    }
    const validItems = psFormItems.filter(it => it.catalog_item_id);
    if (validItems.length === 0) {
      showAlert('入力エラー', '薬を1つ以上追加してください');
      return;
    }

    for (let i = 0; i < validItems.length; i++) {
      const item = validItems[i];
      const group = item.usage_group || '';
      const isFreeInputOnly = FREE_INPUT_ONLY_GROUPS.includes(group);
      const isExternalOrInjection = EXTERNAL_INJECTION_GROUPS.includes(group);
      const missing = [];
      if (!group) missing.push('用法①');
      if (!isFreeInputOnly && !item.dosage_pattern_id) missing.push('用法②');
      if (!item.tablets_per_dose) missing.push(isExternalOrInjection ? '個数' : '量');
      if (!isExternalOrInjection && !item.days) missing.push(group === 'AS_NEEDED' ? '回数' : '日数');
      if (BODY_PART_REQUIRED_GROUPS.includes(group) && !item.body_part?.trim()) missing.push('使用部位');
      if (missing.length > 0) {
        showAlert('入力エラー', `${i + 1}行目: ${missing.join(' / ')} を入力してください`);
        return;
      }
    }

    const patternIdx = new Map(dosagePatterns.map(p => [p.id, p]));
    const payload = validItems.map((it, idx) => {
      const group = it.usage_group || '';
      const isExternalOrInjection = EXTERNAL_INJECTION_GROUPS.includes(group);
      const isAsNeeded = group === 'AS_NEEDED';
      const freeInputOnly = FREE_INPUT_ONLY_GROUPS.includes(group);
      const pattern = it.dosage_pattern_id ? patternIdx.get(Number(it.dosage_pattern_id)) : undefined;
      const dosesPerDay = pattern?.doses_per_day ?? dosesFromGroup(group);
      const fallbackInstruction = INSTRUCTION_BY_USAGE_GROUP[group];
      const fallbackPattern = fallbackInstruction ? dosagePatterns.find(p => p.instruction_text === fallbackInstruction) : undefined;
      const resolvedPatternId = freeInputOnly ? (fallbackPattern?.id || null) : (it.dosage_pattern_id || null);

      return {
        line_uuid: it.line_uuid || generateLineUuid(),
        position: idx + 1,
        catalog_item_id: Number(it.catalog_item_id),
        dosage_pattern_id: resolvedPatternId,
        doses_per_day: dosesPerDay,
        tablets_per_dose: it.tablets_per_dose ? Number(it.tablets_per_dose) : null,
        days: isExternalOrInjection ? 1 : (it.days ? Number(it.days) : null),
        memo: it.memo || '',
        body_part: it.body_part || '',
        as_needed: isAsNeeded,
      };
    });

    setPsSaving(true);
    try {
      if (editingPsId) {
        await axiosInstance.put(`/api/medichat_doctor/prescription_sets/${editingPsId}`, {
          prescription_set: { title: psFormTitle.trim(), items: payload },
        });
      } else {
        await axiosInstance.post('/api/medichat_doctor/prescription_sets', {
          prescription_set: { title: psFormTitle.trim(), items: payload },
        });
      }
      closePsForm();
      await reloadPrescriptionSets();
      showAlert('保存しました', editingPsId ? '処方セットを更新しました' : '処方セットを作成しました');
    } catch (err) {
      showAlert('エラー', err.response?.data?.error || '保存に失敗しました');
    } finally {
      setPsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    setPsDeleting(true);
    try {
      await axiosInstance.delete(`/api/medichat_doctor/prescription_sets/${deleteConfirmId}`);
      setDeleteConfirmId(null);
      await reloadPrescriptionSets();
    } catch (_) {
      showAlert('エラー', '削除に失敗しました');
    } finally {
      setPsDeleting(false);
    }
  };

  // ── プロフィール操作 ──
  const diseaseGroups = useMemo(() => {
    const map = new Map();
    for (const d of diseases) {
      const sp = specialties.find(s => s.id === d.specialty_id);
      const key = sp ? sp.specialty_name : 'その他';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    }
    return Array.from(map.entries());
  }, [diseases, specialties]);

  const symptomGroups = useMemo(() => {
    const map = new Map();
    for (const s of symptoms) {
      if (!map.has(s.category)) map.set(s.category, []);
      map.get(s.category).push(s);
    }
    return Array.from(map.entries());
  }, [symptoms]);

  const updateField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleId = (arr, setArr, id) => {
    const s = new Set(arr);
    s.has(id) ? s.delete(id) : s.add(id);
    setArr([...s]);
  };

  const onUploadClick = () => fileInputRef.current?.click();
  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSelectedFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('doctor[last_name]', form.last_name || '');
    fd.append('doctor[first_name]', form.first_name || '');
    fd.append('doctor[last_name_kana]', form.last_name_kana || '');
    fd.append('doctor[first_name_kana]', form.first_name_kana || '');
    fd.append('doctor[qualifications]', form.qualifications || '');
    fd.append('doctor[experience_years]', String(form.experience_years || 0));
    fd.append('doctor[introduction]', form.introduction || '');
    fd.append('doctor[email]', form.email || '');
    if (form.gender) fd.append('doctor[gender]', form.gender);
    if (selectedFile) fd.append('file', selectedFile);
    selectedDiseaseIds.forEach(id => fd.append('disease_ids[]', id));
    selectedSymptomIds.forEach(id => fd.append('symptom_ids[]', id));
    return fd;
  };

  // プロフィール画像のみ即時保存（それ以外は申請フロー経由）
  const saveImage = async () => {
    if (!selectedFile) {
      showAlert('画像を選択してください', 'プロフィール画像の変更がありません。\n氏名・性別などの変更は「申請する」ボタンをご利用ください。');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      const { data } = await axiosInstance.put('/api/medichat_doctor/profile', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data?.doctor?.profile_image_url) {
        setForm(prev => ({ ...prev, profile_image_url: data.doctor.profile_image_url }));
        setPreviewUrl(data.doctor.profile_image_url);
        setSelectedFile(null);
      }
      showAlert('画像を保存しました', 'プロフィール画像を更新しました。\n氏名・性別などその他の変更は「申請する」ボタンで申請してください。');
    } catch (err) {
      showAlert('エラー', `保存に失敗しました: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axiosInstance.post('/api/medichat_doctor/profile/submit', buildFormData(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showAlert('申請しました', 'プロフィール変更を申請しました。運営の承認後に反映されます。');
    } catch (err) {
      showAlert('エラー', `申請に失敗しました: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const patternIndex = useMemo(
    () => new Map(dosagePatterns.map(p => [p.id, p])),
    [dosagePatterns]
  );

  // ── ローディング ──
  if (loading) return (
    <div>
      <div className="loader" style={{ width: '100px', height: '100px' }} />
      <div style={{ width: '100%', position: 'absolute', bottom: 0, height: '80px', background: '#FEFBF9', zIndex: 12 }} />
    </div>
  );

  // ─── 処方セット: スケルトン ────────────────────────────────
  const renderPsSkeleton = () => (
    <div className="doctorprof-ps-skeleton">
      {[1, 2, 3].map(i => <div key={i} className="doctorprof-ps-skeleton-card" />)}
    </div>
  );

  // ─── 処方セット: タブ内容 ────────────────────────────────
  const renderPrescriptionTab = () => {
    if (psLoading) return renderPsSkeleton();

    if (!psFeatureEnabled) {
      return (
        <div className="doctorprof-ps-disabled">
          <div className="doctorprof-ps-disabled-icon">💊</div>
          <div className="doctorprof-ps-disabled-text">
            処方セット機能は現在ご利用いただけません
          </div>
        </div>
      );
    }

    return (
      <div className="doctorprof-ps-content">
        {/* マイ処方セット */}
        <div className="doctorprof-ps-section-header">
          <span className="doctorprof-ps-section-title">マイ処方セット</span>
          <button className="doctorprof-ps-add-btn" onClick={openCreateForm}>
            ＋ 新規作成
          </button>
        </div>

        {personalSets.length === 0 ? (
          <div className="doctorprof-ps-empty">
            <div className="doctorprof-ps-empty-icon">📋</div>
            <div className="doctorprof-ps-empty-title">処方セットがありません</div>
            <div className="doctorprof-ps-empty-sub">
              よく使う処方パターンをセットとして登録しておくと、診察中の処方入力が素早くなります
            </div>
            <button className="doctorprof-ps-empty-cta" onClick={openCreateForm}>
              最初のセットを作成
            </button>
          </div>
        ) : (
          <div className="doctorprof-ps-list">
            {personalSets.map(set => (
              <div key={set.id} className="doctorprof-ps-card">
                <div className="doctorprof-ps-card-body">
                  <div className="doctorprof-ps-card-title">{set.title}</div>
                  {Array.isArray(set.medication_preview) && set.medication_preview.length > 0 && (
                    <div className="doctorprof-ps-card-drugs">
                      {set.medication_preview.join('・')}
                      {set.item_count > 3 && ` 他${set.item_count - 3}種`}
                    </div>
                  )}
                  <div className="doctorprof-ps-card-meta">💊 {set.item_count ?? 0}種類</div>
                </div>
                <button
                  className="doctorprof-ps-card-menu-btn"
                  onClick={() => setActionSheetSet({ ...set, _isShared: false })}
                  aria-label={`${set.title} のメニューを開く`}
                >
                  ···
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 共通処方セット */}
        {sharedSets.length > 0 && (
          <>
            <div className="doctorprof-ps-section-header" style={{ marginTop: '1.5rem' }}>
              <span className="doctorprof-ps-section-title">共通処方セット</span>
              <span className="doctorprof-ps-section-badge">管理者作成</span>
            </div>
            <div className="doctorprof-ps-list">
              {sharedSets.map(set => (
                <div key={set.id} className="doctorprof-ps-card doctorprof-ps-card--shared">
                  <div className="doctorprof-ps-card-body">
                    <div className="doctorprof-ps-card-title">{set.title}</div>
                    {Array.isArray(set.medication_preview) && set.medication_preview.length > 0 && (
                      <div className="doctorprof-ps-card-drugs">
                        {set.medication_preview.join('・')}
                        {set.item_count > 3 && ` 他${set.item_count - 3}種`}
                      </div>
                    )}
                    <div className="doctorprof-ps-card-meta">💊 {set.item_count ?? 0}種類</div>
                  </div>
                  <button
                    className="doctorprof-ps-card-menu-btn"
                    onClick={() => setActionSheetSet({ ...set, _isShared: true })}
                    aria-label={`${set.title} のメニューを開く`}
                  >
                    ···
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // ─── iOS Action Sheet: カードメニュー ────────────────────
  const renderCardActionSheet = () => {
    if (!actionSheetSet) return null;
    const isEditable = !actionSheetSet._isShared;
    const isCloneable = actionSheetSet._isShared;

    return (
      <div className="doctorprof-overlay" onClick={() => setActionSheetSet(null)}>
        <div className="doctorprof-action-sheet" onClick={e => e.stopPropagation()}>
          <div className="doctorprof-action-sheet-group">
            <div className="doctorprof-action-sheet-title">{actionSheetSet.title}</div>
            {isEditable && (
              <button
                className="doctorprof-action-sheet-item doctorprof-action-sheet-item--default"
                onClick={() => openEditForm(actionSheetSet)}
              >
                編集する
              </button>
            )}
            {isCloneable && (
              <button
                className="doctorprof-action-sheet-item doctorprof-action-sheet-item--default"
                onClick={() => cloneSharedSet(actionSheetSet)}
              >
                コピーしてマイセットに追加
              </button>
            )}
            {isEditable && (
              <button
                className="doctorprof-action-sheet-item doctorprof-action-sheet-item--danger"
                onClick={() => {
                  setDeleteConfirmId(actionSheetSet.id);
                  setActionSheetSet(null);
                }}
              >
                削除する
              </button>
            )}
          </div>
          <button
            className="doctorprof-action-sheet-cancel"
            onClick={() => setActionSheetSet(null)}
          >
            キャンセル
          </button>
        </div>
      </div>
    );
  };

  // ─── iOS Action Sheet: 削除確認 ──────────────────────────
  const renderDeleteConfirmSheet = () => (
    <div className="doctorprof-overlay" onClick={() => setDeleteConfirmId(null)}>
      <div className="doctorprof-action-sheet" onClick={e => e.stopPropagation()}>
        <div className="doctorprof-action-sheet-group">
          <div className="doctorprof-action-sheet-title">
            この処方セットを削除しますか？この操作は取り消せません。
          </div>
          <button
            className="doctorprof-action-sheet-item doctorprof-action-sheet-item--danger"
            onClick={confirmDelete}
            disabled={psDeleting}
          >
            {psDeleting ? '削除中…' : '削除する'}
          </button>
        </div>
        <button
          className="doctorprof-action-sheet-cancel"
          onClick={() => setDeleteConfirmId(null)}
        >
          キャンセル
        </button>
      </div>
    </div>
  );

  // ─── 処方セット: フォームシート ────────────────────────────
  const renderPsFormSheet = () => (
    <div className="doctorprof-overlay" onClick={closePsForm}>
      <div className="doctorprof-ps-sheet" onClick={e => e.stopPropagation()}>
        {/* ヘッダー */}
        <div className="doctorprof-ps-sheet-header">
          <div className="doctorprof-ps-sheet-handle" />
          <div className="doctorprof-ps-sheet-title">
            {editingPsId ? '処方セットを編集' : '処方セットを作成'}
          </div>
          <button className="doctorprof-ps-sheet-close" onClick={closePsForm} aria-label="閉じる">✕</button>
        </div>

        {/* ボディ */}
        <div className="doctorprof-ps-sheet-body">
          {/* セット名 */}
          <div className="doctorprof-ps-field">
            <label className="doctorprof-ps-label">
              セット名<span className="doctorprof-ps-required">*</span>
            </label>
            <input
              className="doctorprof-ps-input"
              value={psFormTitle}
              onChange={e => setPsFormTitle(e.target.value)}
              placeholder="例: 感冒標準セット"
            />
          </div>

          {/* 薬一覧 */}
          <div className="doctorprof-ps-items-header">
            <span className="doctorprof-ps-label">
              薬一覧<span className="doctorprof-ps-required">*</span>
            </span>
            <button
              className="doctorprof-ps-add-item-btn"
              onClick={() => setPsFormItems(prev => [...prev, createEmptyPsItem()])}
            >
              ＋ 薬を追加
            </button>
          </div>

          {psFormItems.length === 0 ? (
            <div className="doctorprof-ps-items-empty">
              「薬を追加」から薬を追加してください
            </div>
          ) : (
            psFormItems.map((item, idx) => {
              const usageGroup = item.usage_group || '';
              const isFreeInputOnly = FREE_INPUT_ONLY_GROUPS.includes(usageGroup);
              const isExternalOrInjection = EXTERNAL_INJECTION_GROUPS.includes(usageGroup);
              const isAsNeeded = usageGroup === 'AS_NEEDED';
              const childOptions = (() => {
                if (!usageGroup || isFreeInputOnly) return [];
                if (usageGroup === 'AS_NEEDED') return dosagePatterns.filter(p => p.default_as_needed);
                const n = dosesFromGroup(usageGroup);
                return dosagePatterns.filter(p => !p.default_as_needed && p.doses_per_day === n);
              })();

              return (
                <div key={item.line_uuid || idx} className="doctorprof-ps-item">
                  <div className="doctorprof-ps-item-top">
                    <span className="doctorprof-ps-item-num">{idx + 1}</span>
                    <button
                      className="doctorprof-ps-item-remove"
                      onClick={() => setPsFormItems(prev => prev.filter((_, i) => i !== idx))}
                      aria-label="この薬を削除"
                    >
                      ✕
                    </button>
                  </div>

                  {/* 薬名 */}
                  <div className="doctorprof-ps-field" style={{ position: 'relative' }}>
                    <label className="doctorprof-ps-label">薬名</label>
                    {item.catalog_item_id ? (
                      <div className="doctorprof-ps-med-selected">
                        <span className="doctorprof-ps-med-chip">💊 {item.catalog_item_name}</span>
                        <button
                          className="doctorprof-ps-med-change"
                          onClick={() => updatePsItem(idx, {
                            catalog_item_id: undefined,
                            catalog_item_name: '',
                            normalized_amount_per_unit: 1,
                            medication_unit: '錠',
                          })}
                        >
                          変更
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          className="doctorprof-ps-input"
                          value={medSearch[idx] || ''}
                          onChange={e => searchMedication(idx, e.target.value)}
                          placeholder="薬品名で検索（2文字以上）"
                        />
                        {(medSuggestions[idx]?.length ?? 0) > 0 && (
                          <div className="doctorprof-ps-suggestions">
                            {medSuggestions[idx].map(med => (
                              <button
                                key={med.id}
                                className="doctorprof-ps-suggestion-item"
                                onClick={() => {
                                  updatePsItem(idx, {
                                    catalog_item_id: med.id,
                                    catalog_item_name: med.name,
                                    normalized_amount_per_unit: med.normalized_amount_per_unit || 1,
                                    medication_unit: med.medication_unit || '錠',
                                  });
                                  setMedSearch(prev => ({ ...prev, [idx]: '' }));
                                  setMedSuggestions(prev => ({ ...prev, [idx]: [] }));
                                }}
                              >
                                <div className="doctorprof-ps-suggestion-name">{med.name}</div>
                                <div className="doctorprof-ps-suggestion-code">{med.code}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* 用法① / 用法② */}
                  <div className="doctorprof-ps-row2">
                    <div className="doctorprof-ps-field">
                      <label className="doctorprof-ps-label">用法①</label>
                      <select
                        className="doctorprof-ps-select"
                        value={usageGroup}
                        onChange={e => {
                          const g = e.target.value;
                          const patch = { usage_group: g, dosage_pattern_id: null, as_needed: g === 'AS_NEEDED' };
                          if (EXTERNAL_INJECTION_GROUPS.includes(g)) patch.days = 1;
                          updatePsItem(idx, patch);
                        }}
                      >
                        <option value="">選択</option>
                        {PARENT_GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </select>
                    </div>
                    <div className="doctorprof-ps-field">
                      <label className="doctorprof-ps-label">用法②</label>
                      {isFreeInputOnly ? (
                        <select className="doctorprof-ps-select" disabled>
                          <option>フリー入力</option>
                        </select>
                      ) : (
                        <select
                          className="doctorprof-ps-select"
                          value={item.dosage_pattern_id || ''}
                          onChange={e => updatePsItem(idx, { dosage_pattern_id: e.target.value ? Number(e.target.value) : null })}
                          disabled={!usageGroup}
                        >
                          <option value="">{usageGroup ? '選択' : '先に用法①を'}</option>
                          {childOptions.map(p => <option key={p.id} value={p.id}>{p.instruction_text}</option>)}
                        </select>
                      )}
                    </div>
                  </div>

                  {/* 量 / 日数 */}
                  <div className="doctorprof-ps-row2">
                    <div className="doctorprof-ps-field">
                      <label className="doctorprof-ps-label">
                        {isExternalOrInjection ? '個数' : isAsNeeded ? '1回量' : '1日量'}
                      </label>
                      <div className="doctorprof-ps-amount-row">
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*\.?[0-9]{0,2}"
                          className="doctorprof-ps-input"
                          value={item.tablets_per_dose ?? ''}
                          onChange={(e) => {
                            const raw = normalizeDoseDraftInput(e.target.value);
                            if (raw === '' || isDoseDraftInput(raw)) {
                              updatePsItem(idx, { tablets_per_dose: raw === '' ? null : raw });
                            }
                          }}
                          onBlur={(e) => {
                            updatePsItem(idx, { tablets_per_dose: normalizeDoseNumber(e.target.value) });
                          }}
                        />
                        {(item.normalized_amount_per_unit ?? 1) > 0 && item.medication_unit && (
                          <span className="doctorprof-ps-unit">×{item.normalized_amount_per_unit}{item.medication_unit}</span>
                        )}
                      </div>
                    </div>
                    {!isExternalOrInjection && (
                      <div className="doctorprof-ps-field">
                        <label className="doctorprof-ps-label">
                          {isAsNeeded ? '回数' : '日数'}
                          <span className="doctorprof-ps-hint">{isAsNeeded ? '' : '（最大90日）'}</span>
                        </label>
                        <div className="doctorprof-ps-amount-row">
                          <input
                            type="number"
                            className="doctorprof-ps-input"
                            min={0}
                            max={isAsNeeded ? MAX_AS_NEEDED_DAYS : 90}
                            value={item.days ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '') {
                                updatePsItem(idx, { days: null });
                                return;
                              }
                              const maxVal = isAsNeeded ? MAX_AS_NEEDED_DAYS : 90;
                              const n = Math.min(maxVal, Math.max(1, Number(v)));
                              updatePsItem(idx, { days: Number.isFinite(n) ? n : null });
                            }}
                          />
                          <span className="doctorprof-ps-unit">{isAsNeeded ? '回分' : '日分'}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 使用部位 */}
                  {BODY_PART_REQUIRED_GROUPS.includes(usageGroup) && (
                    <div className="doctorprof-ps-field">
                      <label className="doctorprof-ps-label">
                        使用部位<span className="doctorprof-ps-required">*</span>
                      </label>
                      <input
                        className="doctorprof-ps-input"
                        placeholder="例: 両目、左耳、患部"
                        value={item.body_part || ''}
                        onChange={e => updatePsItem(idx, { body_part: e.target.value })}
                      />
                    </div>
                  )}

                  {/* メモ */}
                  <div className="doctorprof-ps-field">
                    <label className="doctorprof-ps-label">
                      {isFreeInputOnly ? '用法用量（フリー入力）' : 'メモ'}
                    </label>
                    <input
                      className="doctorprof-ps-input"
                      placeholder={isFreeInputOnly ? '用法用量を自由に記載' : '任意のメモ'}
                      value={item.memo || ''}
                      onChange={e => updatePsItem(idx, { memo: e.target.value })}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* フッター */}
        <div className="doctorprof-ps-sheet-footer">
          <button
            className="doctorprof-btn doctorprof-primary"
            onClick={savePrescriptionSet}
            disabled={psSaving}
            style={{ flex: 1, minHeight: '48px' }}
          >
            {psSaving ? '保存中…' : editingPsId ? '更新する' : '作成する'}
          </button>
          <button
            className="doctorprof-ghost"
            onClick={closePsForm}
            style={{ flex: 'none' }}
          >
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );

  // ─── タブ定義 ────────────────────────────────────────────
  const TABS = [
    { id: 'basic', label: '基本情報' },
    { id: 'diseases', label: '疾患・症状' },
    { id: 'prescriptions', label: '処方セット' },
  ];

  // ─── メインレンダー ─────────────────────────────────────────
  return (
    <div className="doctor-page-shell">
      <div className="doctorprof-container">
        <DoctorPageHeader
          title="プロフィール"
          extra={
            <span className={`doctor-status-pill ${isActive ? 'doctor-status-pill--active' : 'doctor-status-pill--inactive'}`}>
              {isActive ? '稼働中' : '休止中'}
            </span>
          }
        />

        {/* タブバー */}
        <div className="doctorprof-tabbar">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`doctorprof-tab ${activeTab === tab.id ? 'doctorprof-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── タブ1: 基本情報 ── */}
        {activeTab === 'basic' && (
          <section className="doctorprof-section doctorprof-tab-content" key="tab-basic">
            <div className="doctorprof-approval-notice">
              変更内容は申請後、管理者の承認後に反映されます
            </div>
            <div className="doctorprof-grid">
              <div className="doctorprof-row">
                <div className="doctorprof-field-wrapper">
                  <label className="doctorprof-label doctorprof-label-required">氏名（姓）</label>
                  <input className="doctorprof-input" value={form.last_name} onChange={e => updateField('last_name', e.target.value)} />
                </div>
                <div className="doctorprof-field-wrapper">
                  <label className="doctorprof-label doctorprof-label-required">氏名（名）</label>
                  <input className="doctorprof-input" value={form.first_name} onChange={e => updateField('first_name', e.target.value)} />
                </div>
              </div>

              <div className="doctorprof-row">
                <div className="doctorprof-field-wrapper">
                  <label className="doctorprof-label">フリガナ（セイ）</label>
                  <input className="doctorprof-input" value={form.last_name_kana} onChange={e => updateField('last_name_kana', e.target.value)} />
                </div>
                <div className="doctorprof-field-wrapper">
                  <label className="doctorprof-label">フリガナ（メイ）</label>
                  <input className="doctorprof-input" value={form.first_name_kana} onChange={e => updateField('first_name_kana', e.target.value)} />
                </div>
              </div>

              <div className="doctorprof-field-wrapper">
                <label className="doctorprof-label">プロフィール画像</label>
                <div className="doctorprof-upload">
                  <input type="file" accept="image/*" ref={fileInputRef} onChange={onFileChange} style={{ display: 'none' }} />
                  <button type="button" className="doctorprof-upload-btn" onClick={onUploadClick}>
                    {!previewUrl ? (
                      <>
                        <div className="doctorprof-upload-icon">📷</div>
                        <div className="doctorprof-upload-text">タップして写真を選ぶ</div>
                      </>
                    ) : (
                      <div className="doctorprof-avatar-preview">
                        <img
                          src={previewUrl}
                          alt="プロフィール画像"
                          loading="lazy"
                          onError={e => {
                            const def = form.gender === 'female' ? '/images/doctor_female_default.jpeg' : '/images/doctor_male_default.jpeg';
                            if (e.currentTarget.src !== window.location.origin + def) e.currentTarget.src = def;
                          }}
                        />
                      </div>
                    )}
                  </button>
                </div>
              </div>

              <div className="doctorprof-field-wrapper">
                <label className="doctorprof-label">資格</label>
                <textarea className="doctorprof-textarea" rows={3} value={form.qualifications} onChange={e => updateField('qualifications', e.target.value)} />
              </div>

              <div className="doctorprof-row">
                <div className="doctorprof-field-wrapper">
                  <label className="doctorprof-label">経験年数</label>
                  <input className="doctorprof-input-number" type="number" min="0" value={form.experience_years} onChange={e => updateField('experience_years', e.target.value)} />
                </div>
                <div className="doctorprof-field-wrapper">
                  <label className="doctorprof-label">性別</label>
                  <select className="doctorprof-input-number" value={form.gender || ''} onChange={e => updateField('gender', e.target.value)}>
                    <option value="">選択してください</option>
                    <option value="male">男性</option>
                    <option value="female">女性</option>
                  </select>
                </div>
              </div>

              <div className="doctorprof-field-wrapper">
                <label className="doctorprof-label">プロフィール（自己紹介）</label>
                <textarea
                  className="doctorprof-textarea"
                  rows={5}
                  maxLength={100}
                  value={form.introduction}
                  onChange={e => updateField('introduction', e.target.value)}
                  placeholder="100文字以内で入力してください"
                />
                <div style={{ textAlign: 'right', fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {(form.introduction || '').length}/100文字
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── タブ2: 疾患・症状 ── */}
        {activeTab === 'diseases' && (
          <section className="doctorprof-section doctorprof-tab-content" key="tab-diseases">
            <div className="doctorprof-approval-notice">
              変更内容は申請後、管理者の承認後に反映されます
            </div>

            <div className="doctorprof-diseases-block">
              <div className="doctorprof-diseases-heading">対応疾患</div>
              {diseaseGroups.map(([specialtyName, diseaseList]) => (
                <div key={specialtyName} className="doctorprof-category">
                  <div className="doctorprof-category-title">{specialtyName}</div>
                  <div className="doctorprof-options">
                    {diseaseList.map(d => {
                      const isSelected = selectedDiseaseIds.includes(d.id);
                      return (
                        <div
                          key={d.id}
                          className={`doctorprof-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleId(selectedDiseaseIds, setSelectedDiseaseIds, d.id)}
                        >
                          {d.disease_name}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="doctorprof-diseases-block">
              <div className="doctorprof-diseases-heading">対応症状</div>
              {symptomGroups.map(([category, symptomList]) => (
                <div key={category} className="doctorprof-category">
                  <div className="doctorprof-category-title">{category}</div>
                  <div className="doctorprof-options">
                    {symptomList.map(s => {
                      const isSelected = selectedSymptomIds.includes(s.id);
                      return (
                        <div
                          key={s.id}
                          className={`doctorprof-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleId(selectedSymptomIds, setSelectedSymptomIds, s.id)}
                        >
                          {s.symptom_name}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── タブ3: 処方セット ── */}
        {activeTab === 'prescriptions' && (
          <section className="doctorprof-section doctorprof-tab-content" key="tab-prescriptions">
            {renderPrescriptionTab()}
          </section>
        )}

        {/* フッター（基本情報・疾患・症状タブのみ） */}
        {activeTab !== 'prescriptions' && (
          <footer className="doctorprof-footer doctorprof-footer--two">
            {activeTab === 'basic' && (
              <button
                className="doctorprof-ghost"
                onClick={saveImage}
                disabled={saving || submitting || !selectedFile}
                title={!selectedFile ? '画像を選択すると保存できます' : ''}
              >
                {saving ? '保存中…' : '画像を保存'}
              </button>
            )}
            <button
              className="doctorprof-btn doctorprof-primary"
              onClick={submit}
              disabled={submitting || saving}
              style={{ flex: 2, minHeight: '44px' }}
            >
              {submitting ? '申請中…' : '申請する'}
            </button>
          </footer>
        )}

        {/* 処方セット作成・編集シート */}
        {showPsForm && renderPsFormSheet()}

        {/* カードメニュー Action Sheet */}
        {actionSheetSet && renderCardActionSheet()}

        {/* 削除確認 Action Sheet */}
        {deleteConfirmId !== null && renderDeleteConfirmSheet()}

        {/* AlertModal */}
        <AlertModal
          open={alertModal.open}
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
          iconType={alertModal.iconType}
          onClose={closeAlert}
          onConfirm={alertModal.onConfirm}
        />
      </div>
    </div>
  );
};

export default ProfileContainer;
